// @ts-nocheck
import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetBody, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Search, Link2, Building } from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';
import { toast } from 'sonner';

interface LinkClientIdeaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideaId: string;
  onSuccess?: () => void;
}

export function LinkClientIdeaDialog({
  open,
  onOpenChange,
  ideaId,
  onSuccess,
}: LinkClientIdeaDialogProps) {
  const { workspace } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [existingLinks, setExistingLinks] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && workspace) {
      loadItems();
      loadExistingLinks();
    }
  }, [open, workspace, ideaId]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = items.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.industry?.toLowerCase().includes(query)
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [searchQuery, items]);

  const loadItems = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, industry')
        .eq('workspace_id', workspace.id)
        .order('name');

      if (error) throw error;
      setItems(data || []);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients', { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const loadExistingLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('client_ideas')
        .select('client_id')
        .eq('idea_id', ideaId);

      if (error) throw error;

      const linkedIds = new Set<string>(data?.map((link) => link.client_id) || []);
      setExistingLinks(linkedIds);
      setSelectedIds(new Set(linkedIds));
    } catch (error: any) {
      console.error('Error loading existing links:', error);
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const toAdd = Array.from(selectedIds).filter((id) => !existingLinks.has(id));
      const toRemove = Array.from(existingLinks).filter((id) => !selectedIds.has(id));

      if (toAdd.length > 0) {
        const linksToInsert = toAdd.map((clientId) => ({
          idea_id: ideaId,
          client_id: clientId,
          linked_at: new Date().toISOString(),
        }));

        const { error: insertError } = await supabase
          .from('client_ideas')
          .insert(linksToInsert);

        if (insertError) throw insertError;
      }

      if (toRemove.length > 0) {
        for (const clientId of toRemove) {
          const { error: deleteError } = await supabase
            .from('client_ideas')
            .delete()
            .eq('idea_id', ideaId)
            .eq('client_id', clientId);

          if (deleteError) throw deleteError;
        }
      }

      toast.success('Links updated successfully', {
        description: `Added ${toAdd.length}, removed ${toRemove.length}`,
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating links:', error);
      toast.error('Failed to update links', { description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl">
        <SheetHeader>
          <SheetTitle className="flex items-center space-x-2">
            <Link2 className="h-5 w-5" />
            <span>Link Clients to Idea</span>
          </SheetTitle>
          <SheetDescription>
            Select clients that are interested in this idea.
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{selectedIds.size} selected</span>
            {selectedIds.size > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear all
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto border rounded-lg min-h-[400px]">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <Building className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? 'No clients match your search' : 'No clients in workspace'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredItems.map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  const isExisting = existingLinks.has(item.id);

                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleSelection(item.id)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                        isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium">{item.name}</h4>
                            {item.industry && (
                              <div className="mt-1 text-xs text-muted-foreground">{item.industry}</div>
                            )}
                          </div>
                        </div>
                        {isExisting && (
                          <Badge variant="outline" className="ml-2">
                            Already linked
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SheetBody>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : `Link ${selectedIds.size} Clients`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
